import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  FILTERED_LINE_COLOR,
  formatBelowP25LegendLabel,
  formatCapacityBinRange,
  formatNullExcludedLegendLabel,
  getTransmissionCapacityGradientCss,
  TRANSMISSION_CAPACITY_UNIT,
} from '../../../../../utils/transmissionLineCapacity';

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

/**
 * Floating legend for transmission line capacity bins (green → dark blue palette).
 */
const TransmissionLineCapacityMapLegend = ({ capacityBinSpec }) => {
  const belowP25Label = formatBelowP25LegendLabel(capacityBinSpec);
  const nullExcludedLabel = formatNullExcludedLegendLabel(capacityBinSpec);

  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  });

  if (!capacityBinSpec?.hasData && !belowP25Label && !nullExcludedLabel) {
    return null;
  }

  const { bins = [] } = capacityBinSpec;

  return (
    <div
      className="absolute bottom-2 left-2 z-[1000] max-w-[min(100%,14rem)] rounded-md border border-gray-200 bg-white/95 px-2.5 py-2 shadow-md pointer-events-none"
      aria-label="Transmission line capacity legend"
    >
      <div className="pointer-events-auto flex w-full items-start justify-between gap-1.5">
        <p className="text-[10px] font-semibold text-gray-800 mb-0 leading-tight min-w-0">
          Line capacity ({TRANSMISSION_CAPACITY_UNIT})
          {capacityBinSpec.mode === 'discrete' && (
            <span className="font-normal text-gray-500"> · one class per value</span>
          )}
          {capacityBinSpec.mode === 'grouped' && (
            <span className="font-normal text-gray-500"> · grouped values</span>
          )}
        </p>
        <button
          type="button"
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-300"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse line capacity legend' : 'Expand line capacity legend'}
        >
          {isOpen ? (
            <ChevronUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          )}
        </button>
      </div>

      {bins.length > 0 && (
        <div
          className={`h-2 w-full rounded-sm mt-1.5 ${isOpen ? 'mb-1.5' : 'mb-0'}`}
          style={{ background: `linear-gradient(to right, ${getTransmissionCapacityGradientCss()})` }}
        />
      )}

      {isOpen && (
        <>
          {bins.length > 0 && (
            <ul className="space-y-1">
              {bins.map((bin) => (
                <li key={bin.layerKey} className="flex items-center gap-1.5 text-[10px] text-gray-700 leading-tight">
                  <span
                    className="shrink-0 w-5 h-0.5 rounded-full"
                    style={{ backgroundColor: bin.color }}
                  />
                  <span className="min-w-0 truncate" title={formatCapacityBinRange(bin)}>
                    {formatCapacityBinRange(bin)}
                  </span>
                  <span className="text-gray-400 shrink-0">({bin.count})</span>
                </li>
              ))}
            </ul>
          )}
          {(belowP25Label || nullExcludedLabel) && (
            <div className="space-y-1 mt-1 pt-1 border-t border-gray-100">
              {belowP25Label && (
                <div className="flex items-center gap-1.5 text-[10px] text-gray-600 leading-tight">
                  <span
                    className="shrink-0 w-5 h-0.5 rounded-full"
                    style={{ backgroundColor: FILTERED_LINE_COLOR, opacity: 0.85 }}
                  />
                  <span>{belowP25Label}</span>
                </div>
              )}
              {nullExcludedLabel && (
                <div className="flex items-center gap-1.5 text-[10px] text-gray-600 leading-tight">
                  <span
                    className="shrink-0 w-5 h-0.5 rounded-full"
                    style={{ backgroundColor: FILTERED_LINE_COLOR, opacity: 0.85 }}
                  />
                  <span>{nullExcludedLabel}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransmissionLineCapacityMapLegend;
